import { faFamily, faHeart, faUpload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Modal,
  Button,
  Row,
  Col,
  Typography,
  Input,
  Form,
  Upload,
  Avatar,
  Select,
} from "antd";
import AntdImgCrop from "antd-img-crop";
import { useEffect } from "react";
import { useContext, useState } from "react";
import { useUserMutation } from "../../api/Users";
import { useProfileProgress } from "../../hooks/useProfileProgress";
import { AuthContext } from "../../useAuth";
import { useImagePresignUrl } from "../../api/Images";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";

const { useForm } = Form;

dayjs.extend(timezone);

const UserTypePrompt = () => {
  const { user } = useContext(AuthContext);

  const showPrompt = user && !user?.UserType;
  const userProgress = useProfileProgress(user);
  const [currentStep, setCurrentStep] = useState(0);
  const [destination, setDestination] = useState();

  const [open, setOpen] = useState(true);
  const queryClient = useQueryClient();

  const close = () => {
    setOpen(false);
  };

  const navigate = useNavigate();
  const nextStep = () => {
    if (currentStep + 1 == steps.length) {
      close();
      queryClient.invalidateQueries(["users", user?.UserId]);
      if (destination) navigate(destination);
    } else setCurrentStep(currentStep + 1);
  };

  const steps = [
    {
      content: (
        <UserType
          userId={user?.UserId}
          next={nextStep}
          setDestination={setDestination}
        />
      ),
    },
  ];
  if (!userProgress.hasBase) {
    steps.push({
      content: <UserProfile next={nextStep} user={user} />,
    });
  }

  return !showPrompt ? null : (
    <Modal
      open={open}
      onCancel={() => close()}
      title="Set up your profile"
      closable={false}
      maskClosable={false}
      footer={null}
    >
      {steps[currentStep].content}
    </Modal>
  );
};

const UserType = ({ userId, next, setDestination }) => {
  const [selectedItem, setSelectedItem] = useState();
  const [submitting, setSubmitting] = useState(false);
  const userUpdate = useUserMutation(userId, false);

  const toggleItem = (value) => {
    setSelectedItem(value);
    let destination = value === "parent" ? "/caregivers" : "/caregivers/apply";
    setDestination(destination);
  };

  const submit = async () => {
    setSubmitting(true);
    userUpdate
      .mutateAsync({ UserType: selectedItem, Timezone: dayjs.tz.guess() })
      .then(() => next())
      .finally(() => setSubmitting(false));
  };
  return (
    <Form onFinish={submit}>
      <Typography.Paragraph>I am a:</Typography.Paragraph>
      <Form.Item>
        <Row gutter={20}>
          <Col span={12}>
            <OptionButton
              title="Parent/User"
              description={"I'm here to learn"}
              icon={faFamily}
              selected={selectedItem === "parent"}
              onClick={() => toggleItem("parent")}
            />
          </Col>
          <Col span={12}>
            <OptionButton
              title="Provider"
              description={"I'm here to teach"}
              icon={faHeart}
              selected={selectedItem === "caregiver"}
              onClick={() => toggleItem("caregiver")}
            />
          </Col>
        </Row>
      </Form.Item>
      <Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          disabled={!selectedItem || submitting}
          block
        >
          Next
        </Button>
      </Form.Item>
    </Form>
  );
};

const UserProfile = ({ user, next }) => {
  const [submitting, setSubmitting] = useState(false);
  const userUpdate = useUserMutation(user?.UserId, false);
  const presignUrl = useImagePresignUrl();
  const [image, setImage] = useState();
  const [imagePreview, setImagePreview] = useState();
  const [form] = useForm();

  useEffect(() => {
    if (image) setImagePreview(URL.createObjectURL(image));
  }, [image]);

  const submit = async (values) => {
    setSubmitting(true);

    if (image)
      await presignUrl
        .mutateAsync({
          entityId: user?.UserId,
          entityType: "userAvatar",
          name: image?.name,
          imageType: "image/jpg",
        })
        .then(async (r) => {
          const { url, imageKey } = r;
          axios.put(url, image).then(() => {
            values.Avatar = imageKey;
          });
        })
        .catch(() => {
          setSubmitting(false);
        });

    userUpdate.mutateAsync(values).then(() => {
      next();
    });
  };
  return (
    <Form layout="vertical" onFinish={submit} form={form} initialValues={user}>
      <Form.Item name="Avatar" label="Upload a photo">
        <AntdImgCrop>
          <Upload
            beforeUpload={(file) => {
              form.setFieldValue(
                "Avatar",
                `userAvatar/${user?.UserId}/${file.name}`
              );
              setImage(file);
              return false;
            }}
            maxCount={1}
            showUploadList={false}
            multiple={false}
          >
            <Avatar
              shape="square"
              size={100}
              icon={<FontAwesomeIcon icon={faUpload} />}
              src={imagePreview}
            />
          </Upload>
        </AntdImgCrop>
      </Form.Item>
      <Form.Item name="FirstName" label="First Name">
        <Input />
      </Form.Item>
      <Form.Item name="LastName" label="Last Name">
        <Input />
      </Form.Item>
      <Form.Item name="Timezone" label="Timezone">
        <Select
          options={Intl.supportedValuesOf("timeZone").map((t) => ({
            label: t,
            value: t,
          }))}
          showSearch
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" block disabled={submitting}>
          Next
        </Button>
      </Form.Item>
      {/* <Form.Item>
        <Button block type="text" onClick={() => next()}>
          I'll do this later
        </Button>
      </Form.Item> */}
    </Form>
  );
};

const OptionButton = ({ title, description, icon, selected, onClick }) => {
  const click = () => {
    onClick && onClick();
  };

  return (
    <div
      style={{
        border: "1px solid #dedede",
        padding: "20px",
        textAlign: "center",
        borderColor: selected && "#9D79BC",
        color: selected && "#9D79BC",
        cursor: "pointer",
      }}
      onClick={() => click()}
    >
      <Typography.Title level={1} style={{ margin: "0", color: "inherit" }}>
        <FontAwesomeIcon icon={icon} />
      </Typography.Title>
      <Typography.Title level={4} style={{ margin: "0", color: "inherit" }}>
        {title}
      </Typography.Title>
      <Typography.Text style={{ color: "inherit" }}>
        {description}
      </Typography.Text>
    </div>
  );
};

export default UserTypePrompt;
